import { useState } from 'react';
import { Menu } from 'lucide-react';

import { BrandMark } from '@/components/BrandMark';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

interface NavProps {
  onJoinWaitlist: (location: string) => void;
}

const NAV_LINKS = [
  { href: '#engine', label: 'The engine' },
  { href: '#mission', label: 'Mission' },
  { href: '#trust', label: 'Glass box' },
  { href: '#access', label: 'Access' },
  { href: '#faq', label: 'FAQ' },
];

function scrollToAnchor(href: string) {
  document
    .querySelector(href)
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function Nav({ onJoinWaitlist }: NavProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  const handleAnchorClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    event.preventDefault();
    setMenuOpen(false);
    scrollToAnchor(href);
  };

  return (
    <header className="sticky top-0 z-30 border-b border-white/8 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-6xl items-center gap-4 px-6 py-3">
        <a
          href="#top"
          onClick={(event) => handleAnchorClick(event, '#top')}
          className="flex shrink-0 items-center"
          aria-label="NovaQuantis home"
        >
          <BrandMark wordmarkSize={22} />
        </a>

        <nav className="ml-auto hidden items-center gap-7 md:flex">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(event) => handleAnchorClick(event, link.href)}
              className="mkt-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground transition-colors hover:text-foreground"
            >
              {link.label}
            </a>
          ))}
          <Button size="sm" onClick={() => onJoinWaitlist('nav')}>
            Join the waitlist
          </Button>
        </nav>

        <div className="ml-auto md:hidden">
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Open menu">
                <Menu />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="flex flex-col gap-2">
              <SheetHeader>
                <SheetTitle>NovaQuantis</SheetTitle>
              </SheetHeader>
              <nav className="mt-2 flex flex-col">
                {NAV_LINKS.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(event) => handleAnchorClick(event, link.href)}
                    className="mkt-mono border-b border-white/8 py-3 text-sm uppercase tracking-[0.14em] text-foreground transition-colors hover:text-primary"
                  >
                    {link.label}
                  </a>
                ))}
              </nav>
              <SheetClose asChild>
                <Button className="mt-4" onClick={() => onJoinWaitlist('nav_mobile')}>
                  Join the waitlist
                </Button>
              </SheetClose>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
